import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type { ProfileMedia } from "../../types/profile";

/**
 * GALLERY のハッシュタグ / フリーワード作品検索 API スライス。
 *
 * プロフィール作品に設定されたハッシュタグ・作品名・アーティスト名を検索
 * キーに、公開中プロフィール（slug あり + 公開版あり）の作品を横断一覧
 * する。表示対象の公開範囲は BE が閲覧者で切り替える（通常ログイン:
 * public + private、デモ閲覧アカウント: public のみ）。BE 仕様は
 * soremo-soundcheck `docs/api/gallery-works-search.md` を参照。
 */

// ===== 型定義 =====

/** タグ候補（ヒット件数付き）。 */
export interface GalleryWorkTag {
  name: string;
  /** ヒット件数（作品数。肩書き候補は該当ユーザー数）。 */
  works_count: number;
  /**
   * 管理側でピン留めされたマスタータグ（作品 0 件でも先頭に常時表示）。
   * BE は常に返すが、後方互換のため optional。
   */
  pinned?: boolean;
}

/** 検索結果作品のクリエイター情報（プロフィールページへの誘導用）。 */
export interface GalleryWorkCreator {
  slug: string;
  display_name: string;
  avatar_url: string | null;
}

/**
 * アーティストカード（アーティスト名・肩書きにヒットしたクリエイター。
 * 肩書きはタグ完全一致でも q 部分一致でも返る）。
 * title はユーザーの肩書き（title_jp / title_en）、statement は公開版
 * プロフィールのステートメント（theme_jp / theme_en）抜粋。
 */
export interface GalleryCreator {
  slug: string;
  display_name: string;
  title: string;
  avatar_url: string | null;
  statement: string;
}

/** 検索結果の作品 1 件。media は代表 1 件のみ。 */
export interface GalleryWork {
  id: string;
  title: string;
  created_year: string;
  credit: string;
  content_type: string;
  image_url: string | null;
  youtube_link: string | null;
  tags: string[];
  media: ProfileMedia[];
  /** 字幕テキスト（「[M:SS] テキスト」の行形式。鑑賞モードのテロップに使う）。 */
  subtitle_text?: string;
  creator: GalleryWorkCreator;
}

interface GalleryWorkTagsResponse {
  status: string;
  data: { tags: GalleryWorkTag[] };
}

interface GalleryWorksResponse {
  status: string;
  /**
   * tag / q には使用された検索条件が入る（未使用側は null）。
   * creators はアーティスト名・肩書きヒットのアーティストカード。
   * 肩書き（title_jp / title_en）への一致はユーザー本人のヒットとして
   * creators のみに入り、関連作品は works に含まれない（tag / q 共通）。
   */
  data: {
    tag: string | null;
    q: string | null;
    works: GalleryWork[];
    creators: GalleryCreator[];
  };
}

/**
 * 作品検索のパラメータ。どちらか一方を指定する。
 * - `tag`: タグ名の完全一致（タグチップ選択時）
 * - `q`: フリーワード（タグ完全一致 + 作品名部分一致 +
 *   アーティスト名・肩書き部分一致の和集合）
 */
export type GalleryWorksParams = { tag: string; q?: never } | { q: string; tag?: never };

// ===== API スライス =====

export const galleryApi = createApi({
  reducerPath: "galleryApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => ({
    /** タグ候補（q 空なら使用数上位）。 */
    getGalleryWorkTags: builder.query<GalleryWorkTagsResponse, { q?: string }>({
      query: ({ q }) => ({
        url: "/api/gallery/work-tags",
        params: q ? { q } : undefined,
      }),
    }),
    /** 作品横断一覧（tag = タグ完全一致 / q = タグ + 作品名のフリーワード）。 */
    getGalleryWorks: builder.query<GalleryWorksResponse, GalleryWorksParams>({
      query: (params) => ({
        url: "/api/gallery/works",
        params: params.tag != null ? { tag: params.tag } : { q: params.q },
      }),
    }),
  }),
});

export const { useGetGalleryWorkTagsQuery, useGetGalleryWorksQuery } = galleryApi;
